<svelte:options tag={null} />

<script lang="ts">
  import type { PriceType } from './price.types';

  export let light = false;
  export let type: PriceType = 'default';
  export let mainprice: string;
  export let mainpricearialabel: string | undefined = undefined;
  export let mainpriceunit: string | undefined = undefined;
  export let secondarypricelabel: string | undefined = undefined;
  export let secondarypriceariallabel: string | undefined = undefined;
  export let flagamount: string | undefined = undefined;
  export let flaglabel: string | undefined = undefined;
  export let flagarialabel: string | undefined = undefined;
  export let priceenddatelabel: string | undefined = undefined;
  export let pricewithoutoffer: string | undefined = undefined;
  export let pricewithoutofferarialabel: string | undefined = undefined;
  export let refundsofferproductlabel: string | undefined = undefined;
  export let refundsoffermediaurl: string | undefined = undefined;

  $: isDiscount = type === 'destocking' || type === 'commercial-operation';
  $: isDisplayFlag =
    type === 'budget-price' ||
    type === 'best-value' ||
    type === 'high-quality' ||
    type === 'star-product';
</script>

<div
  class="mc-price {$$props.class}"
  class:light
  class:mc-price--discount={isDiscount}
  {...$$restProps}
>
  {#if isDisplayFlag}
    <span class="price__flag--{type}">
      {flaglabel}
    </span>
  {:else if type === 'destocking'}
    <div class="price__flag--clearance" aria-label={flagarialabel}>
      <span class="price__flag--clearance-label">{flaglabel}</span>
      <span class="price__flag--clearance-amount">{flagamount}</span>
    </div>
  {:else if type === 'commercial-operation'}
    <div class="price__flag--discount" aria-label={flagarialabel}>
      <span class="price__flag--discount-amount">{flagamount}</span>
      <span class="price__flag--discount-label">{priceenddatelabel}</span>
    </div>
  {/if}

  {#if isDiscount}
    <div class="price__from" aria-label={pricewithoutofferarialabel}>
      <span class="price__from-without-offer">{pricewithoutoffer}</span>
    </div>
  {/if}

  <div class="price">
    <div class="price__main">
      <span data-cerberus="ELEM_PRIX" aria-label={mainpricearialabel}>
        {mainprice}
      </span>
      {#if mainpriceunit}
        <span class="price__main__unit">/ {mainpriceunit}</span>
      {/if}
    </div>
    {#if secondarypricelabel}
      <span class="price__secondary" aria-label={secondarypriceariallabel}>
        {secondarypricelabel}
      </span>
    {/if}
  </div>

  {#if !light && refundsofferproductlabel}
    {#if refundsoffermediaurl}
      <a
        class="mc-tag-link mc-tag-link__label"
        href={refundsoffermediaurl}
        target="_blank"
        >{refundsofferproductlabel}
      </a>
    {:else}
      <span class="mc-tag-text mc-tag-text--danger mc-tag-text__label">
        {refundsofferproductlabel}
      </span>
    {/if}
  {/if}
</div>

<style lang="scss">
  @import '@mozaic-ds/styles/settings-tools/_all-settings';
  @import '@mozaic-ds/styles/components/c.tag';

  .mc-price {
    .price__flag {
      &--budget-price,
      &--best-value,
      &--high-quality,
      &--star-product {
        @include set-font-face('semi-bold');
        @include set-font-scale('02', 'm');
        display: inline-block;
        padding: $mu050;
        margin-bottom: $mu025;
        line-height: 0.5rem;
        text-transform: uppercase;
      }
      &--budget-price {
        background-color: $color-secondary-yellow-500;
      }
      &--best-value {
        background-color: $color-primary-01-500;
      }
      &--high-quality {
        background-color: $color-secondary-blue-500;
      }
      &--star-product {
        padding: $mu025 $mu050;
        line-height: 1rem;
        color: #fff;
        text-transform: none;
        background-color: $color-secondary-red-500;
      }
      &--clearance {
        display: flex;
        &-label {
          @include set-font-scale('05', 'm');
          display: inline-block;
          vertical-align: middle;
          padding: calc(#{$mu025} - 1px) $mu050;
          border: get-border(s) solid $color-secondary-red-500;
          line-height: 1.25rem;
          text-transform: uppercase;
          color: $color-secondary-red-500;
        }
        &-amount {
          @include set-font-face('semi-bold');
          @include set-font-scale('05', 'm');
          display: inline-block;
          vertical-align: middle;
          padding: $mu025 $mu050;
          background-color: $color-secondary-red-500;
          color: $color-secondary-yellow-500;
          line-height: 1.25rem;
        }
      }
      &--discount {
        &-amount {
          @include set-font-face('semi-bold');
          @include set-font-scale('05', 'm');
          display: inline-block;
          vertical-align: middle;
          padding: $mu025 $mu050;
          background-color: $color-secondary-red-500;
          line-height: 1.25rem;
          color: #fff;
        }
        &-label {
          @include set-font-scale('02', 'm');
          display: inline-block;
          vertical-align: middle;
          margin-left: $mu050;
          color: $color-secondary-red-500;
        }
      }
    }
    .price__from {
      line-height: 1.75rem;
      &-without-offer {
        @include set-font-scale('04', 'm');
        display: inline-block;
        vertical-align: middle;
        color: $color-grey-999;
        text-decoration: line-through;
      }
    }
    .price__main {
      @include set-font-face('semi-bold');
      @include set-font-scale('09', 'm');
      &__unit {
        @include set-font-scale('05', 'm');
      }
    }
    .price__secondary {
      @include set-font-scale('02', 'm');
      color: $color-grey-900;
    }

    &.mc-price--discount {
      .price__main {
        color: $color-secondary-red-500;
      }
    }

    &.light {
      .price__flag {
        &--budget-price,
        &--best-value,
        &--high-quality {
          padding: $mu025;
          line-height: 0.75rem;
        }
        &--clearance {
          display: flex;
          &-label {
            @include set-font-scale('02', 'm');
            line-height: 0.75rem;
          }
          &-amount {
            @include set-font-scale('02', 'm');
            line-height: 0.75rem;
            padding: calc(#{$mu025} - 1px) $mu025;
          }
        }
      }
      .price__main {
        @include set-font-scale('07', 'm');
        &__unit {
          @include set-font-scale('04', 'm');
        }
      }
    }
  }
</style>
